import { Component } from '@theme/component';

/**
 * A swipeable / draggable image carousel.
 *
 * The track is a scroll-snap container, so touch swiping, trackpad scrolling and
 * keyboard scrolling all work with this script disabled. Everything here is
 * progressive enhancement on top of that: dots, arrows, mouse dragging and
 * arrow-key stepping.
 *
 * @typedef {object} MediaCarouselRefs
 * @property {HTMLElement} track - The scroll-snap container holding the slides
 * @property {HTMLButtonElement[]} [dots] - One button per slide
 * @property {HTMLButtonElement} [prev] - Previous-slide arrow
 * @property {HTMLButtonElement} [next] - Next-slide arrow
 *
 * @extends {Component<MediaCarouselRefs>}
 */
export class MediaCarouselComponent extends Component {
  requiredRefs = ['track'];

  /** @type {HTMLElement[]} */
  #slides = [];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {Array<() => void>} */
  #teardown = [];

  #active = 0;

  /** Whether the pointer moved far enough to count as a drag rather than a click. */
  #moved = false;

  connectedCallback() {
    super.connectedCallback();

    const { track } = this.refs;
    this.#slides = /** @type {HTMLElement[]} */ ([...track.children]);
    if (this.#slides.length < 2) return;

    this.#bindDots();
    this.#bindArrows();
    this.#bindDrag();
    this.#bindKeys();
    this.#watchActiveSlide();
    this.#syncControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#observer = undefined;
    for (const off of this.#teardown) off();
    this.#teardown = [];
  }

  /** @param {EventTarget} target @param {string} type @param {EventListener} fn @param {object} [opts] */
  #on(target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    this.#teardown.push(() => target.removeEventListener(type, fn, opts));
  }

  #bindDots() {
    const dots = this.refs.dots ?? [];
    if (dots.length !== this.#slides.length) return;

    dots.forEach((dot, index) => {
      this.#on(dot, 'click', () => this.#goTo(index));
    });
  }

  #bindArrows() {
    const { prev, next } = this.refs;
    if (prev) this.#on(prev, 'click', () => this.#goTo(this.#active - 1));
    if (next) this.#on(next, 'click', () => this.#goTo(this.#active + 1));
  }

  #bindKeys() {
    this.#on(this.refs.track, 'keydown', (/** @type {KeyboardEvent} */ event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.#goTo(this.#active + (event.key === 'ArrowRight' ? 1 : -1));
    });
  }

  /**
   * Click-and-drag panning for mouse and pen.
   *
   * Touch is deliberately left alone: the browser's own momentum and rubber-band
   * scrolling beat anything reimplemented here.
   */
  #bindDrag() {
    const { track } = this.refs;
    let dragging = false;
    let startX = 0;
    let startLeft = 0;
    let pointerId = /** @type {number | null} */ (null);

    this.#on(track, 'pointerdown', (/** @type {PointerEvent} */ event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;

      dragging = true;
      this.#moved = false;
      startX = event.clientX;
      startLeft = track.scrollLeft;
      pointerId = event.pointerId;
      track.classList.add('media-carousel__track--dragging');

      // Mandatory snapping fights every incremental scrollLeft write, so the
      // drag would stutter. It is restored on release, which re-snaps.
      track.style.scrollSnapType = 'none';
      track.setPointerCapture(pointerId);
    });

    this.#on(track, 'pointermove', (/** @type {PointerEvent} */ event) => {
      if (!dragging || event.pointerId !== pointerId) return;

      const dx = event.clientX - startX;
      if (Math.abs(dx) > 4) this.#moved = true;
      track.scrollLeft = startLeft - dx;
      event.preventDefault();
    });

    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('media-carousel__track--dragging');
      track.style.scrollSnapType = '';

      if (pointerId !== null && track.hasPointerCapture(pointerId)) {
        track.releasePointerCapture(pointerId);
      }
      pointerId = null;

      // Restoring snap-type does not re-snap on its own when the pointer simply
      // stops, so settle on the closest slide explicitly.
      if (this.#moved) this.#goTo(this.#nearestIndex());
    };

    this.#on(track, 'pointerup', endDrag);
    this.#on(track, 'pointercancel', endDrag);

    // A drag that ends on a link must not activate it.
    this.#on(
      track,
      'click',
      (/** @type {MouseEvent} */ event) => {
        if (!this.#moved) return;
        event.preventDefault();
        event.stopPropagation();
        this.#moved = false;
      },
      true
    );

    // Native image dragging would hijack the gesture.
    this.#on(track, 'dragstart', (/** @type {DragEvent} */ event) => event.preventDefault());
  }

  #watchActiveSlide() {
    // `root: track` scopes intersection to the scroll container, so the active
    // slide is the one filling the track rather than the one in the viewport.
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = this.#slides.indexOf(/** @type {HTMLElement} */ (entry.target));
          if (index === -1 || index === this.#active) continue;
          this.#active = index;
          this.#syncControls();
        }
      },
      { root: this.refs.track, threshold: 0.6 }
    );

    for (const slide of this.#slides) this.#observer.observe(slide);
  }

  #syncControls() {
    const dots = this.refs.dots ?? [];
    dots.forEach((dot, index) => {
      dot.setAttribute('aria-current', index === this.#active ? 'true' : 'false');
    });

    const { prev, next } = this.refs;
    if (prev) prev.disabled = this.#active === 0;
    if (next) next.disabled = this.#active === this.#slides.length - 1;
  }

  #nearestIndex() {
    const { track } = this.refs;
    const target = track.scrollLeft + track.offsetLeft;
    let nearest = 0;
    let best = Infinity;

    this.#slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - target);
      if (distance < best) {
        best = distance;
        nearest = index;
      }
    });

    return nearest;
  }

  /** @param {number} index */
  #goTo(index) {
    const clamped = Math.max(0, Math.min(index, this.#slides.length - 1));
    const slide = this.#slides[clamped];
    if (!slide) return;

    const { track } = this.refs;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // scrollLeft on the track, not slide.scrollIntoView(), which would also
    // scroll the page to bring the carousel into view.
    track.scrollTo({
      left: slide.offsetLeft - track.offsetLeft,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });

    this.#active = clamped;
    this.#syncControls();
  }
}

if (!customElements.get('media-carousel-component')) {
  customElements.define('media-carousel-component', MediaCarouselComponent);
}
